import React from "react"
import styled, { css, keyframes } from "styled-components"
import { connect } from "react-redux"
import { AutoSuggest, ButtonLink } from "buoy-core"
import { responsive, colors } from "buoy-theme"
import Source from "../Source"
import { captureException } from "../../common/error"
import { onSubmit as _onSubmit, onChange as _onChange } from "../handlers"
import { setSearchActive, specialistInfoSubmit } from "../../search/actions"

const SearchOuterContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: none;
  width: 100%;
  opacity: 1;
  padding-left: 0;
  position: relative;

  > * {
    width: 100%;
    max-width: 496px;
    margin-left: auto;
    margin-right: auto;
  }

  ${props =>
    props.focused
      ? css`
          position: ${props => (props.editorActive ? "absolute" : "fixed")};
          margin: none;
          height: 100%;
          left: 0;
          right: 0;
          overflow-y: scroll;
          box-sizing: border-box;
          padding: 0px 16px 80px;

          ${responsive("top", "68px", "68px", "59px", "56px")};

          background: ${props => (props.postIntent ? colors.primary : "white")};
          animation: ${fade} 0.4s ease;

          ${props =>
            props.postIntent
              ? css`
                  div > div {
                    background: ${colors.primary};

                    &:hover {
                      background: ${colors.primary};
                    }
                  }
                  p {
                    color: white;
                  }
                `
              : ""};

          form {
            min-height: 48px;
            button {
              color: ${props.postIntent ? "white" : colors.neutralDarkest};
            }
          }
        `
      : css`
          form {
            margin: 12px 0 8px;
          }
        `};

  form + p:nth-child(2) {
    padding-left: 16px;
    ${responsive("margin-top", "20px", "20px", "20px", "10px")};
  }

  form + div:nth-child(2),
  p + div:nth-child(3) {
    height: auto;
    box-sizing: border-box;
  }

  ${props =>
    !props.focused &&
    `
    animation: ${fade1} 0.4s ease;
  `};
`

const fade = keyframes`
  0%  { opacity: 0 }
  100%  { opacity: 1 }
`

const fade1 = keyframes`
  0%,1%  { opacity: 0 }
  100%  { opacity: 1 }
`

const Skip = styled(ButtonLink).attrs({
  color: "neutralDarkest",
  fontSize: "bodyMain",
})`
  float: right;
`

class SpecialistResponse extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      suggestions: [],
      active: -1,
      searchFocus: false,
    }
  }

  componentDidMount() {
    if (typeof document !== "undefined") {
      const input = document.getElementById("search-input")
      input.addEventListener("keydown", this.keyDownInput, false)
      input.addEventListener("focus", this.onFocus, false)
    }
  }

  componentWillUnmount() {
    if (typeof document !== "undefined") {
      const input = document.getElementById("search-input")
      input.removeEventListener("keydown", this.keyDownInput, false)
      input.removeEventListener("focus", this.onFocus, false)
    }
  }

  keyDownInput = e => {
    let key = e.keyCode || e.which
    switch (key) {
      case 13: // Return
        e.preventDefault()
        e.stopPropagation()
        if (this.state.active >= 0) {
          this.handleSuggestionClick(
            this.state.suggestions[this.state.active].text
          )
        } else if (this.props.value.length > 1) {
          this.props.onSubmitPostIntent(true)
        }
        break
      case 38: // Arrow up
        e.preventDefault()
        e.stopPropagation()
        this.decrement()
        break
      case 40: // Arrown down
        e.preventDefault()
        e.stopPropagation()
        this.increment()
        break
      default:
        break
    }
  }

  increment() {
    if (this.state.suggestions.length) {
      let index =
        this.state.active >= this.state.suggestions.length - 1
          ? 0
          : this.state.active + 1
      this.setState({ active: index })
    }
  }

  decrement() {
    if (this.state.suggestions.length) {
      let index =
        this.state.active <= 0
          ? this.state.suggestions.length - 1
          : this.state.active - 1
      this.setState({ active: index })
    }
  }

  handleChange = e => {
    e.preventDefault()
    e.stopPropagation()
    let query = e.target.value
    this.props.onChange({ display: query, name: "" })
    if (query.length > 1) {
      Source.getSpecialists(query)
        .then(data => {
          let specialists = []
          data.suggestions.forEach(specialist =>
            specialists.push({
              text: specialist.display,
              token: specialist.name,
            })
          )
          this.setState({ suggestions: specialists })
        })
        .catch(captureException)
    }
  }

  handleSuggestionClick = name => {
    const { suggestions } = this.state
    const specialist = suggestions.find(o => o.text === name)

    this.props.onChange({ display: specialist.text, name: specialist.token })
    this.setState({ suggestions: [], active: -1, searchFocus: false })
    this.props.onSubmitPostIntent(false)
  }

  handleClear = () => {
    this.props.onChange({ display: "", name: "" })
    document.getElementById("search-input").focus()
  }

  onFocus = () => {
    this.props.setSearchActive(true)
    this.setState({ searchFocus: true })
  }

  onCancel = () => {
    this.props.setSearchActive(false)
    this.setState({ searchFocus: false })
  }

  render() {
    const { searchFocus, suggestions, active } = this.state
    const { postIntent, placeholder, item, onSkip, noSkip } = this.props
    return (
      <React.Fragment>
        <SearchOuterContainer
          postIntent={postIntent}
          focused={searchFocus}
          {...this.props}
        >
          <AutoSuggest
            placeholder={placeholder || item.placeholder}
            suggestions={suggestions}
            suggestionClick={this.handleSuggestionClick}
            value={this.props.value}
            showCancel={searchFocus}
            onCancel={this.onCancel}
            selected={0}
            activeSuggestion={active}
            onChange={this.handleChange}
            onClear={this.handleClear}
            setListRef={() => () => true}
            setSuggestionRef={() => () => true}
            showDefaults={false}
            showSuggestionHeader={false}
          />
        </SearchOuterContainer>
        {!noSkip && <Skip onClick={onSkip}>Skip</Skip>}
      </React.Fragment>
    )
  }
}

const mapStateToProps = state => ({
  value: state.integration.specialist.display,
})

const mapDispatchToProps = (dispatch, ownProps) => {
  const onSubmit = _onSubmit(ownProps.item, dispatch)
  const onChange = _onChange(ownProps.item, dispatch)

  return {
    onSubmit,
    onSubmitPostIntent: suggestion => {
      dispatch(specialistInfoSubmit(suggestion, ownProps.postIntent))
    },
    onChange: name => onChange(name),
    onSkip: () => {
      onChange("")
      onSubmit()
    },
    setSearchActive: state => {
      dispatch(setSearchActive(state))
    },
  }
}

const SpecialistResponseWrapper = connect(mapStateToProps, mapDispatchToProps)(
  SpecialistResponse
)

export { SpecialistResponseWrapper as default, SpecialistResponse }



// WEBPACK FOOTER //
// ./assets/interview/components/SpecialistResponse.js